/**
 * @file inward-ding.ts
 * @module app/modules/inward/utils/inward-ding
 * @description Overdue reminder sound: a short two-tone "ding" made with the Web Audio API (no
 *   sound file), and the rule for when the next ding is due. The time of the last ding is kept in
 *   localStorage so a page reload or a second tab does not ding again before the interval.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

export const LAST_DING_KEY = "inwardSlaLastDing";

type AudioContextClass = typeof AudioContext;

let context: AudioContext | null = null;

/** Shared audio context (null when the browser has no Web Audio). */
export const audioContext = (): AudioContext | null => {
  if (context) return context;
  const w = window as unknown as { AudioContext?: AudioContextClass; webkitAudioContext?: AudioContextClass };
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  context = Ctor ? new Ctor() : null;
  return context;
};

/** Browsers start audio "suspended" until the user interacts with the page: resume on interaction. */
export const unlockAudio = (): void => {
  const ctx = audioContext();
  if (ctx && ctx.state === "suspended") ctx.resume().catch(() => undefined);
};

/** Plays the ding. Returns false when sound is not possible yet (no Web Audio or not unlocked). */
export const playDing = (): boolean => {
  const ctx = audioContext();
  if (!ctx || ctx.state !== "running") return false;
  const start = ctx.currentTime;
  [
    { freq: 1318.5, at: 0 }, // E6
    { freq: 987.8, at: 0.18 }, // B5
  ].forEach(({ freq, at }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, start + at);
    gain.gain.exponentialRampToValueAtTime(0.35, start + at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + at + 1.1);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start + at);
    osc.stop(start + at + 1.2);
  });
  return true;
};

/** Time of the last ding (0 when never / unreadable). */
export const lastDingAt = (): number => {
  try {
    const v = Number(window.localStorage.getItem(LAST_DING_KEY));
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
};

export const rememberDing = (at: number): void => {
  try {
    window.localStorage.setItem(LAST_DING_KEY, String(at));
  } catch {
    // private mode / storage full: the next ding just follows this tab's timer
  }
};

/** A ding is due when nothing has dinged yet, or the interval has passed since the last one. */
export const dingDue = (last: number, now: number, intervalMinutes: number): boolean =>
  !last || now - last >= Math.max(1, intervalMinutes) * 60_000;
